import ApiBase from './Api'
import { getStoredLanguage, resolveLanguage } from '../i18n/languagePreference'
import {
    DeleteIssueRequest,
    GetIssueByCompanyRequest,
    GetIssueByShareRequest,
    GetIssueListRequest,
    GetIssueSingleItemRequest,
    GetMcIssuesListRequest,
    PatchIssueRequest,
    PatchMcIssuesRequest,
    PostIssueRequest,
    PutIssueRequest,
} from '@finance-lab/utils/types'

const ISSUE_API_PATHS = {
    ISSUES: '/issues',
    ISSUE: (id: string) => `/issues/${id}`,
    ADMIN_ISSUES: '/admin/issues',
    ADMIN_ISSUE: (id: string) => `/admin/issues/${id}`,

    ADMIN_MCISSUES: '/admin/mcissues',
    ADMIN_MCISSUE: (id: string) => `/admin/mcissues/${id}`,
}

class IssueApi extends ApiBase {
    async findAll() {
        const fetchedIssues = await this._getRequest<GetIssueListRequest>(ISSUE_API_PATHS.ISSUES)

        return fetchedIssues
    }

    async findByIssueId(issueId: string, options?: { rawInvestorAmounts?: boolean }) {
        if (!issueId) {
            return null
        }
        const preferredLanguage = resolveLanguage(getStoredLanguage())
        const fetchedIssue = await this._getRequest<GetIssueSingleItemRequest>(ISSUE_API_PATHS.ISSUE(issueId), {
            queryParameters: {
                preferredLanguage,
                ...(options?.rawInvestorAmounts ? { rawInvestorAmounts: 'true' } : {}),
            },
        })

        return fetchedIssue
    }

    async findByCompanyId(companyId: string, { excludeCancelled = false }: { excludeCancelled?: boolean } = {}) {
        if (!companyId) {
            return []
        }
        const fetchedIssues = await this._getRequest<GetIssueByCompanyRequest>(ISSUE_API_PATHS.ISSUES, {
            queryParameters: {
                companyId,
                ...(excludeCancelled ? { excludeCancelled: 'true' } : {}),
            },
        })

        return fetchedIssues
    }
    async findByShareId(shareId: string | null) {
        if (!shareId) {
            return []
        }
        const fetchedIssues = await this._getRequest<GetIssueByShareRequest>(ISSUE_API_PATHS.ISSUES, {
            queryParameters: { shareId },
        })
        return fetchedIssues
    }

    async create(data: PostIssueRequest['Body']) {
        if (!data) {
            return null
        }

        const createdIssue = await this._postRequest<PostIssueRequest>(ISSUE_API_PATHS.ADMIN_ISSUES, data)

        return createdIssue
    }

    async update(issueId: string, data: PutIssueRequest['Body']) {
        if (!data) {
            return null
        }

        const updatedIssue = await this._putRequest<PutIssueRequest>(ISSUE_API_PATHS.ADMIN_ISSUE(issueId), data)

        return updatedIssue
    }

    /**
     * Changes specific fields of an issue. The difference between this method and the update method is that this method
     * only updates the fields that are passed in the data object. The update method, on the other hand, updates all the fields
     * of the issue.
     * Note that only certain fields are allowed to be updated with the PATCH method. Currently only the isinUnits and isinRights
     * fields are allowed.
     */
    async patchFields(issueId: string, data: Exclude<PatchIssueRequest['Body'], { matchedTo: string }>) {
        if (!data || Object.keys(data).length === 0) {
            return null
        }
        // whitelistedFields are the fields that are allowed to be updated with the PATCH method
        const whitelistedFields = ['isinUnits', 'isinRights'] as const
        const fieldsToUpdate: Record<string, string | null> = {}
        whitelistedFields.forEach((field) => {
            if (data[field] !== undefined) {
                fieldsToUpdate[field] = data[field]
            }
        })
        if (Object.keys(fieldsToUpdate).length === 0) {
            console.error('No whitelisted fields to update. ', data)
            return null
        }
        const updatedIssue = await this._patchRequest<PatchIssueRequest>(
            ISSUE_API_PATHS.ADMIN_ISSUE(issueId),
            fieldsToUpdate,
        )
        return updatedIssue
    }

    async delete(issueId: string) {
        const deletedIssue = await this._deleteRequest<DeleteIssueRequest>(ISSUE_API_PATHS.ADMIN_ISSUE(issueId))
        return deletedIssue
    }

    async findMcIssues() {
        const fetchedMcIssues = await this._getRequest<GetMcIssuesListRequest>(ISSUE_API_PATHS.ADMIN_MCISSUES)

        return fetchedMcIssues
    }

    async archiveMcIssue(instrumentHistoryId: string, flagAsConvertible = false): Promise<null> {
        await this._patchRequest<PatchMcIssuesRequest>(ISSUE_API_PATHS.ADMIN_MCISSUE(instrumentHistoryId), {
            archived: true,
            isConvertible: flagAsConvertible,
        })
        return null
    }

    /**
     * Records the match between an issue and an instrument history event,
     * which indicates that we have handled this instrument history as an issue.
     */
    async matchIssueToInstrumentHistory(issueId: string, instrumentHistoryId: string) {
        const matchedResponse = await this._patchRequest<PatchIssueRequest>(ISSUE_API_PATHS.ADMIN_ISSUE(issueId), {
            matchedTo: instrumentHistoryId,
        })
        return matchedResponse
    }
}

export default IssueApi
